import { Link } from "react-router-dom";
import { MapPin, Clock, Phone, Mail } from "lucide-react";
import { PageHeader, Reveal } from "@/components/site/Reveal";
import { InquiryForm } from "@/components/site/InquiryForm";
import { useContent } from "@/context/ContentContext";
import { IMG, fileUrl } from "@/lib/images";

export function About() {
  const c = useContent();
  const s = c?.settings || {};
  const imgs = (c?.images || []).filter((i) => i.category === "about");
  const [main, second] = imgs;
  return (
    <div data-testid="about-page">
      <PageHeader eyebrow="Om Liisa" title="Kunnskap, kreativitet, håndverk – og kjærlighet til det unike" testid="about-header" />
      <section className="px-6 md:px-16 lg:px-24 grid lg:grid-cols-12 gap-14 items-start">
        <Reveal className="lg:col-span-5 relative">
          <img src={main ? fileUrl(main.storage_path) : IMG.pins} alt={main?.caption || "Liisa"} className="w-full h-[560px] object-cover object-top" data-testid="about-main-image" />
          {second && <img src={fileUrl(second.storage_path)} alt={second.caption} className="hidden md:block absolute -bottom-12 -right-10 w-48 h-64 object-cover border-8 border-[#FDFBF7]" data-testid="about-second-image" />}
        </Reveal>
        <Reveal className="lg:col-span-6 lg:col-start-7" delay={0.1}>
          <p className="font-serif-display text-3xl md:text-4xl leading-snug italic text-[#8C6E48]">«Et plagg som sitter perfekt forandrer hvordan du føler deg.»</p>
          <p className="mt-8 text-[#5C5A56] leading-relaxed text-base md:text-lg" data-testid="about-text">{s.about_text}</p>
          <Link to="/bestill" className="inline-block mt-10 px-8 py-4 bg-[#1A1A1A] text-[#FDFBF7] text-sm hover:bg-[#8C6E48] transition-colors" data-testid="about-book-btn">Møt Liisa – bestill prøvetime</Link>
        </Reveal>
      </section>
    </div>
  );
}

const GALLERY = [
  [IMG.alterWomen, "Opplegg og justering – bukser, kjoler, ermer", "md:col-span-2 md:row-span-2"],
  [IMG.festwear, "Festantrekk", ""],
  [IMG.alterMen, "Dress og blazer", ""],
  [IMG.bridalPoster, "Skreddersydde brudekjoler", "md:row-span-2"],
  [IMG.tools, "Detaljer", ""],
  [IMG.machine, "I atelieret", ""],
];

export function Gallery() {
  const c = useContent();
  const uploaded = (c?.images || []).filter((i) => i.category === "gallery");
  const items = uploaded.length
    ? uploaded.map((i, n) => [fileUrl(i.storage_path), i.caption, GALLERY[n % GALLERY.length][2]])
    : GALLERY;
  return (
    <div data-testid="gallery-page">
      <PageHeader eyebrow="Galleri" title="Fra atelieret" testid="gallery-header" />
      <section className="px-6 md:px-16 lg:px-24 grid md:grid-cols-3 auto-rows-[300px] gap-5">
        {items.map(([src, label, cls], i) => (
          <figure key={i} className={`group relative overflow-hidden ${cls}`} data-testid={`gallery-item-${i}`}>
            <img src={src} alt={label} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000" />
            <figcaption className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/60 to-transparent text-white text-sm opacity-0 group-hover:opacity-100 transition-opacity">{label}</figcaption>
          </figure>
        ))}
      </section>
    </div>
  );
}

export function Contact() {
  const s = useContent()?.settings || {};
  const rows = [
    [MapPin, s.address],
    [Phone, s.phone],
    [Mail, s.email],
  ].filter(([, v]) => v);
  return (
    <div data-testid="contact-page">
      <PageHeader eyebrow="Kontakt" title="Velkommen innom atelieret" intro="Andre tider etter avtale. Har du et spørsmål med en gang? Spør AI-assistenten nede til høyre." testid="contact-header" />
      <section className="px-6 md:px-16 lg:px-24 grid lg:grid-cols-12 gap-14">
        <Reveal className="lg:col-span-4 space-y-6" >
          <div data-testid="contact-info" className="space-y-5">
            {rows.map(([Icon, v]) => <p key={v} className="flex gap-4 items-start"><Icon className="w-5 h-5 text-[#8C6E48] mt-0.5" strokeWidth={1.5} />{v}</p>)}
            <div className="flex gap-4 items-start">
              <Clock className="w-5 h-5 text-[#8C6E48] mt-0.5" strokeWidth={1.5} />
              <div className="space-y-1 text-sm flex-1">{(s.hours || []).map((h) => <p key={h.day} className="flex justify-between"><span>{h.day}</span><span>{h.time}</span></p>)}</div>
            </div>
          </div>
        </Reveal>
        <Reveal className="lg:col-span-7 lg:col-start-6" delay={0.1}><InquiryForm defaultType="kontakt" /></Reveal>
      </section>
    </div>
  );
}

export function Privacy() {
  return (
    <div data-testid="privacy-page">
      <PageHeader eyebrow="Personvern" title="Personvernerklæring" testid="privacy-header" />
      <section className="px-6 md:px-16 lg:px-24 max-w-3xl space-y-5 text-[#5C5A56] leading-relaxed">
        <p>SømSignature by Liisa Lipasina behandler personopplysninger (navn, e-post, telefon og innholdet i din henvendelse) kun for å besvare forespørsler og gjennomføre oppdrag.</p>
        <p>Samtaler med AI-assistenten lagres for å kunne følge opp spørsmål og forbedre kundeservicen. Del ikke sensitive opplysninger i chatten.</p>
        <p>Opplysningene deles ikke med tredjeparter utover tekniske leverandører som er nødvendige for driften (f.eks. booking via SumUp). Du kan når som helst be om innsyn i eller sletting av dine opplysninger ved å kontakte oss.</p>
      </section>
    </div>
  );
}
